<template>
  <div class="overflow-x-clip bg-neutral-50">
    <Head v-if="specialisation" :title="specialisation.name" />

    <div v-if="specialisation" class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <!-- Breadcrumb: Strona główna / Specjalizacje / Nazwa -->
      <nav class="mb-8 flex min-w-0 flex-wrap items-center gap-1 text-sm" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <Link :href="specialisationsIndexHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.specialisations') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="min-w-0 break-words text-[#2ea8cf]">{{ specialisation.name }}</span>
      </nav>

      <div class="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        <!-- Left column: content cards -->
        <div class="flex min-w-0 flex-col gap-6">
          <!-- 1. Header card: clinic badge + name -->
          <article
            id="specjalizacja"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <div v-if="site_context_label" class="flex min-w-0 flex-wrap gap-2">
              <NeutralPill :label="site_context_label" />
            </div>
            <h1 class="min-w-0 break-words text-xl font-medium leading-7 tracking-[-0.02em] text-neutral-900">
              {{ specialisation.name }}
            </h1>
          </article>

          <!-- 2. O specjalizacji -->
          <section
            v-if="specialisation.description"
            id="o-specjalizacji"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('specialisations.about_heading') }}
            </h2>
            <ExpandableText :text="specialisation.description" />
          </section>

          <!-- 3. Konsultacje / Zabiegi / Badania -->
          <ServiceCategoryList
            v-if="specialisation.services?.length"
            :services="specialisation.services"
          />

          <!-- 4. FAQ -->
          <section
            v-if="specialisation.faq?.length"
            id="faq"
            class="scroll-mt-[48px] flex min-w-0 flex-col gap-3 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:scroll-mt-[64px] lg:p-6"
          >
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('faq.title') }}
            </h2>
            <div class="flex min-w-0 flex-col gap-3">
              <details
                v-for="(item, index) in specialisation.faq"
                :key="index"
                class="group min-w-0 rounded-lg border border-neutral-200 bg-white p-4"
              >
                <summary
                  class="flex cursor-pointer list-none items-center justify-between gap-3 text-base font-medium leading-6 text-neutral-900"
                >
                  <span class="min-w-0 break-words">{{ item.question }}</span>
                  <svg
                    class="size-5 shrink-0 text-neutral-500 transition-transform group-open:rotate-180"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 7.5 10 12.5 15 7.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </summary>
                <p
                  v-if="item.answer"
                  class="mt-4 break-words border-t border-neutral-100 pt-4 text-base leading-6 text-neutral-700 whitespace-pre-line"
                >
                  {{ item.answer }}
                </p>
              </details>
            </div>
          </section>
        </div>

        <!-- Right column: specialists in this specialisation -->
        <aside
          v-if="specialisation.specialists?.length"
          id="lekarze"
          class="min-w-0 scroll-mt-[48px] lg:sticky lg:self-start lg:scroll-mt-[64px] transition-[top] duration-300 ease-out"
          :style="{ top: `${stickyPanelTop}px` }"
        >
          <section class="flex min-w-0 flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_6px_7.5px_rgba(16,24,40,0.08)] lg:p-6">
            <h2 class="text-sm font-semibold uppercase tracking-[1px] text-neutral-500">
              {{ $t('specialisations.doctors_heading') }}
            </h2>
            <ul class="flex min-w-0 flex-col divide-y divide-neutral-200">
              <li
                v-for="specialist in specialisation.specialists"
                :key="specialist.id"
                class="flex min-w-0 flex-col gap-3 pt-4 first:pt-0 last:pb-0 lg:flex-row lg:flex-wrap lg:items-center"
              >
                <SpecialistCard :specialist="specialist" flat class="min-w-0 flex-1" />
                <Link
                  :href="bookingHref(specialist)"
                  class="flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3.5 text-base font-semibold leading-[18px] text-white shadow-[0px_2px_3px_rgba(16,24,40,0.06)] transition-opacity hover:opacity-90 lg:w-auto"
                  :style="{ backgroundImage: brandGradient }"
                >
                  {{ $t('common.book_appointment') }}
                </Link>
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>

    <div v-else class="mx-auto max-w-[1200px] px-4 py-16 text-center text-neutral-500">
      {{ $t('specialisations.not_found') }}
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import ExpandableText from '@/Shared/ExpandableText.vue';
import NeutralPill from '@/Shared/NeutralPill.vue';
import SpecialistCard from '@/Shared/SpecialistCard.vue';
import ServiceCategoryList from '@/Shared/ServiceCategoryList.vue';
import { stickyPanelTop } from '@/utils/stickyMainHeader';
import { BRAND_GRADIENT } from '@/utils/siteThemeColors';

export default {
  name: 'SpecialisationShow',
  components: { Head, Link, ExpandableText, NeutralPill, SpecialistCard, ServiceCategoryList },
  props: {
    specialisation: { type: Object, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context: { type: String, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: { type: String, default: '' },
  },
  data() {
    return {
      brandGradient: BRAND_GRADIENT,
    };
  },
  computed: {
    stickyPanelTop() {
      return stickyPanelTop.value;
    },
    effectiveSiteContext() {
      return this.specialisation?.site_context || this.site_context || 'code_medical_center';
    },
    homeHref() {
      const ctx = this.effectiveSiteContext;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[ctx]) {
        return this.$routes[ctx]();
      }
      return '/';
    },
    specialisationsIndexHref() {
      const ctx = this.effectiveSiteContext;
      const routeName = `${ctx}_specialisations`;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[routeName]) {
        return this.$routes[routeName]();
      }
      return this.$routes.specialisations_index();
    },
  },
  methods: {
    bookingHref(specialist) {
      return `${this.$routes.specialist(specialist.slug)}#umow-wizyte`;
    },
  },
};
</script>
